import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { DefaultValues, FieldValues, Path, useForm } from 'react-hook-form'
import { z } from 'zod'

import { FORM_MODE } from '../../utils/constants/form.constants'

const useMultipleSchema = <T extends string[], U extends FieldValues>(
  data: { name: T[number]; schema: z.ZodSchema }[], // List of form names and corresponding schemas
  onSubmit: (values: U, name: T[number]) => Promise<void>, // Submit handler
  defaultValues?: DefaultValues<U> // Default values for the form
) => {
  const [formState, setFormState] = useState<T[number] | null>(null) // Track the active form state
  const [validationSchema, setValidationSchema] = useState<z.ZodSchema | null>(null) // Store the active schema
  const [loading, setLoading] = useState(() =>
    data.reduce(
      (acc, item) => {
        acc[item.name] = false // Initialize loading state for each form
        return acc
      },
      {} as Record<T[number], boolean>
    )
  ) // Optional loading state for async operations

  const form = useForm<U>({
    resolver: validationSchema ? zodResolver(validationSchema) : undefined,
    defaultValues,
    mode: FORM_MODE
  })

  // Get the schema for the provided form name
  const getSchema = (name: T[number]): z.ZodSchema | null => {
    const schemaData = data.find((item) => item.name === name)
    return schemaData ? schemaData.schema : null // Return schema if found
  }

  // Submit the form and handle validation based on the schema
  const submitForm = async (name: T[number]) => {
    const schema = getSchema(name)
    if (!schema) throw new Error(`Schema for form "${name}" not found`) // Handle missing schema

    setFormState(name) // Set the current form state
    setValidationSchema(schema) // Set the validation schema for the current form

    form.clearErrors() // Clear previous errors

    const values = form.getValues()
    const validationResult = schema.safeParse(values)

    if (!validationResult.success) {
      // Handle validation errors and map them to form errors
      const errors = validationResult.error.errors.reduce(
        (acc, error) => {
          // Handle errors with valid paths
          if (error.path && error.path.length > 0) {
            const fieldKey = error.path[0] as string // Use the first path segment as the key
            acc[fieldKey] = { message: error.message }
          } else if (error.code === 'invalid_union' && 'unionErrors' in error) {
            // Handle union errors by extracting field errors from the first union branch
            const unionError = error.unionErrors?.[0]
            if (unionError?.issues) {
              unionError.issues.forEach((issue) => {
                if (issue.path && issue.path.length > 0) {
                  const fieldKey = issue.path[0] as string
                  acc[fieldKey] = { message: issue.message }
                }
              })
            }
          } else {
            // Handle other errors without specific paths
            console.warn('Validation error without path:', error)
          }
          return acc
        },
        {} as Record<string, { message: string }>
      )

      console.error('Validation errors:', errors)

      // Set individual field errors in the form
      Object.keys(errors).forEach((field) => {
        form.setError(field as Path<U>, { message: errors[field].message })
      })

      // Optionally set a root error (general form error)
      form.setError('root', { message: 'Veuillez corriger les erreurs avant de sauvegarder.' })
      return
    }

    // If validation passes, call the onSubmit handler with the transformed data
    try {
      setLoading((prev) => ({ ...prev, [name]: true })) // Set loading state for the current form
      await onSubmit(validationResult.data as U, name)
      setLoading((prev) => ({ ...prev, [name]: false })) // Reset loading state after submission
    } catch (error) {
      console.error('Submission error:', error)
      setLoading((prev) => ({ ...prev, [name]: false })) // Reset loading state on error
      form.setError('root', { message: 'Une erreur est survenue lors de la soumission.' }) // Set a root error
    }
  }

  return { form, loading, formState, validationSchema, submitForm, getSchema }
}

export default useMultipleSchema
